'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '@/contexts/LanguageContext';

interface NavigationItem {
  label: string;
  href: string;
  badge?: string;
}

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isEmergencyPulsing, setIsEmergencyPulsing] = useState(true);
  const { t } = useLanguage();

  const navigationItems: NavigationItem[] = [
    { label: t('common.home'), href: '/homepage' },
    { label: t('common.services'), href: '/services-overview' },
    { label: t('common.industries'), href: '/industry-solutions' },
    { label: t('common.about'), href: '/about' },
    { label: t('common.contact'), href: '/contact' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card shadow-card">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo Section */}
          <Link href="/homepage" className="flex items-center space-x-2 group">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center transform transition-transform duration-300 group-hover:scale-105">
                <span className="text-white font-headline font-bold text-xl">CS</span>
              </div>
            </div>
            <span className="font-headline font-bold text-lg text-primary leading-tight">
              Centronics IT Solutions
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="px-4 py-2 text-sm font-body font-semibold text-muted-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-200"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right Section - Emergency & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {/* 24/7 Emergency Support Indicator */}
            <div className="flex items-center space-x-2 px-3 py-2 bg-error/10 rounded-lg border border-error/20">
              <div className={`w-2 h-2 bg-error rounded-full ${isEmergencyPulsing ? 'animate-pulse-slow' : ''}`} />
              <span className="text-xs font-cta font-semibold text-error">{t('common.support247')}</span>
            </div>

            {/* Client Portal Link */}
            <Link
              href="/client-portal"
              className="flex items-center space-x-2 px-4 py-2 text-sm font-cta font-semibold text-primary hover:text-brand-primary transition-colors duration-200"
            >
              <Icon name="UserCircleIcon" size={20} />
              <span>{t('common.portal')}</span>
            </Link>

            {/* Primary CTA */}
            <Link
              href="/contact"
              className="flex items-center space-x-2 px-6 py-2.5 bg-brand-conversion hover:bg-brand-conversion/90 text-white rounded-lg font-cta font-semibold text-sm shadow-card hover:shadow-elevated transition-all duration-300 transform hover:scale-105"
            >
              <Icon name="CalendarIcon" size={18} variant="outline" />
              <span>{t('common.freeAssessment')}</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-primary hover:bg-muted rounded-md transition-colors duration-200"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border shadow-elevated">
          <nav className="container mx-auto px-4 py-4 space-y-1">
            {/* Language Switcher - Mobile */}
            <div className="mb-4">
              <LanguageSwitcher />
            </div>
            
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-base font-body font-semibold text-muted-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-200"
              >
                {item.label}
              </Link>
            ))}

            {/* Mobile Emergency Support */}
            <div className="flex items-center justify-between px-4 py-3 bg-error/10 rounded-lg border border-error/20 mt-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-error rounded-full animate-pulse-slow" />
                <span className="text-sm font-cta font-semibold text-error">{t('common.support247')}</span>
              </div>
              <Icon name="PhoneIcon" size={20} className="text-error" />
            </div>

            {/* Mobile Client Portal */}
            <Link
              href="/client-portal"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-between px-4 py-3 text-base font-body font-semibold text-primary hover:bg-muted rounded-md transition-all duration-200 mt-2"
            >
              <span>{t('common.portal')}</span>
              <Icon name="UserCircleIcon" size={20} />
            </Link>

            {/* Mobile CTA */}
            <Link
              href="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center space-x-2 w-full px-6 py-3 bg-brand-conversion hover:bg-brand-conversion/90 text-white rounded-lg font-cta font-semibold text-base shadow-card mt-4"
            >
              <Icon name="CalendarIcon" size={20} variant="outline" />
              <span>{t('common.freeAssessment')}</span>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;